// The Standards Library as the UI sees it. Mirrors the backend's utils/verity/store.ts — one shape,
// two languages; if you change a field here, change it there.

/** Where a standard has force, outermost first: ["Canada", "Ontario", "Collingwood"]. */
export type JurisdictionPath = string[];

/**
 * Whether the library holds the standard's content or only its name.
 *
 * `reference` is the prototype's own distinction and worth keeping: a named standard with no rules
 * behind it, so a finding can say "governed by OPSD 701.010, which this library does not hold" rather
 * than pass in silence.
 */
export type StandardType = "ingested" | "reference";

/**
 * Whether the section tree is believable for a document this size — mirrors assessStructure on the
 * server. `implausible` holds requirement extraction until a person has looked.
 */
export type StructureVerdict = "ok" | "warning" | "implausible";

export interface StructureIssue {
  code: string;
  severity: "warning" | "implausible";
  detail: string;
}

export interface StructureAssessment {
  verdict: StructureVerdict;
  issues: StructureIssue[];
  metrics: {
    textPages: number;
    characters: number;
    sections: number;
    chapters: number;
    pagesPerSection: number;
    pagesPerChapter: number;
    coverage: number;
    malformedTitles: number;
  };
}

/** How far through ingestion a standard has got. */
export type IngestState = "uploaded" | "extracting" | "extracted" | "failed";

export interface ExtractionSummary {
  extractionVersion: number;
  pdfjsVersion: string;
  extractedAt: string;
  /** How many files the standard arrived as. */
  documentCount: number;
  pageCount: number;
  textPageCount: number;
  /** Pages with no text layer at all. Shown, never hidden — it is the honest limit of coverage. */
  rasterPageCount: number;
  characterCount: number;
  sectionCount: number;
  chapterCount: number;
  appendixCount: number;
  subsectionCount: number;
  rasterOnlySectionCount: number;
  /** Whether the structure is believable for a document this size. Mirrors assessStructure. */
  structure?: StructureAssessment;
}

export interface VerityStandard {
  id: string;
  /** 'awp' for an AWP standard; absent means engineering — every standard made before AWP existed. */
  reviewType?: "awp";
  jurisdictionPath: JurisdictionPath;
  authority: string;
  title: string;
  shortCode?: string;
  edition: string;
  domain: string;
  type: StandardType;
  relevance?: string;

  /** Every file the standard is made of, in order. Absent on a reference row. */
  documents?: StandardDocument[];
  // The first document, repeated flat, so the library table need not reach into an array.
  docId?: string;
  fileName?: string;
  fileSize?: number;

  state: IngestState;
  extraction?: ExtractionSummary;
  extractionError?: string;

  /** Which fields a person confirmed when the standard was added, and when. */
  review?: {
    verifiedFields: string[];
    reviewedAt: string;
    reviewedBy: string;
  };

  createdAt: string;
  updatedAt: string;
}

// ─── What the reader proposes ──────────────────────────────────────────────────

/**
 * How the reader came by a value.
 *
 * The distinction is the whole point of showing it. `found` can be checked against the page it was
 * quoted from; `inferred` is the reader's reasoning and needs a person to agree with it; `suggested`
 * appears nowhere in the document at all; `generated` is not a claim about the document — it is an
 * identifier the library minted, like the short code, and asking how confident anyone is in it would
 * be a category error. A verify screen that flattened these into one "extracted" label would invite a
 * reviewer to wave through a value the document never contained.
 */
export type FieldProvenance = "found" | "inferred" | "suggested" | "generated";

export interface ProposedField {
  value: string;
  /** 0–1. An ambiguous cover page is meant to look ambiguous here. */
  confidence: number;
  provenance: FieldProvenance;
  /** Verbatim from the document, and verified to exist in it. Only ever set when provenance is `found`. */
  quote?: string;
  pdfPage?: number;
  /** What the reader wants looked at — an ambiguity, or the basis of an inference. */
  note?: string;
}

export interface ProposedMetadata {
  /** One entry per level, outermost first. Each level carries its own provenance. */
  jurisdictionPath: ProposedField[];
  authority: ProposedField;
  title: ProposedField;
  edition: ProposedField;
  shortCode: ProposedField;
  domain: ProposedField;
}

export interface DocumentStructure {
  pageCount: number;
  textPageCount: number;
  rasterPageCount: number;
  headingCount: number;
  characterCount: number;
}

/**
 * One file of a standard.
 *
 * A standard is not always one PDF — authorities split them by volume or issue appendices separately,
 * and those parts are one standard with one edition. Each part keeps its own page numbering, so a
 * citation names the part as well as the page.
 */
export interface StandardDocument {
  docId: string;
  blobName: string;
  fileName: string;
  fileSize: number;
  /** Position within the standard. Part 0 carries the cover page the reader takes metadata from. */
  docIndex: number;
}

/** What comes back from reading, before anything is added to the library. */
export interface ReadResult {
  documents: (StandardDocument & { structure: DocumentStructure })[];
  metadata: ProposedMetadata;
  /** Totals across every part. */
  structure: DocumentStructure & { documentCount: number };
}

/** The confirmed standard, as submitted to the library. */
export interface ConfirmedStandard extends StandardIntake {
  /** Sent when added from the AWP side. Absent means engineering. */
  reviewType?: "awp";
  documents: StandardDocument[];
  /** The field keys the person actually confirmed. */
  verifiedFields: string[];
}

export type SectionKind = "chapter" | "subsection" | "appendix";

export interface VeritySection {
  sectionId: string;
  parentId: string | null;
  /** Which part of the standard this section is in — each part keeps its own page numbering. */
  docId: string;
  docIndex: number;
  numbering: string;
  title: string;
  kind: SectionKind;
  depth: number;
  order: number;
  path: string[];
  pdfPageStart: number;
  pdfPageEnd: number;
  /** What a citation quotes. Null where those pages carry no printed number. */
  printedPageStart: number | null;
  printedPageEnd: number | null;
  charStart: number;
  charEnd: number;
  pageCount: number;
  rasterPageCount: number;
  hasText: boolean;
}

/** What the intake form collects. Every field is typed by a person; none is read from the document. */
export interface StandardIntake {
  jurisdictionPath: JurisdictionPath;
  authority: string;
  title: string;
  shortCode: string;
  edition: string;
  domain: string;
  type: StandardType;
  relevance: string;
}

// ─── The interpretation layer ──────────────────────────────────────────────────

export type RuleType =
  | "dimensional" | "calculation" | "material" | "drawing-content" | "submission"
  | "procedural" | "prohibition" | "lookup" | "reference" | "definitional";

export type CheckStrategy = "deterministic" | "evidence-presence" | "llm-judgement" | "manual";

/** What a person decided. Absent means nobody has needed to — which is the normal case. */
export type HumanDecision = "approved" | "edited" | "excluded" | "needs-review";

/**
 * What the machine could establish about the EXTRACTION — never about how hard the requirement will
 * be to check later. Those are different questions at different stages, and conflating them made 60%
 * of chapter 7 look like it needed a person when 5% did.
 */
export type MachineValidation = "verified" | "review-recommended" | "action-required";

export type AttentionReason =
  | "low-confidence" | "span-unverified" | "incomplete-predicate"
  | "type-predicate-mismatch" | "incomplete-lookup" | "dangling-relation"
  | "unsourced-obligation" | "raster-source" | "overlapping-rule";

export const ATTENTION_COPY: Record<AttentionReason, string> = {
  "low-confidence": "The extractor was unsure of this reading.",
  "span-unverified": "The quoted sentence could not be located in the source.",
  "incomplete-predicate": "Stated as a threshold but missing its value or unit.",
  "type-predicate-mismatch": "Classified as dimensional but carries no measurable predicate.",
  "incomplete-lookup": "A table row has neither a value nor a reference.",
  "dangling-relation": "Points at a related requirement that no longer exists.",
  "unsourced-obligation": "Cites another document without stating what is actually required.",
  "raster-source": "The page it came from carries no text layer.",
  "overlapping-rule": "Another requirement here quotes the same sentence and reads alike.",
};

/** How a rule came to exist. A reviewer who splits or merges has authored, not merely edited. */
export type RuleDerivation = "extracted" | "split" | "merge" | "manual" | "recovered";

export type RelationType = "sibling-range" | "exception-path" | "constrains";

export interface RuleRelation {
  type: RelationType;
  targetRuleId: string;
}

export interface LookupRow {
  key: string;
  value?: number | string;
  /** A cell can name another standard instead of carrying a value. */
  ref?: string;
}

export interface RulePredicate {
  kind: "threshold" | "compound" | "lookup" | "none";
  operator?: ">=" | "<=" | ">" | "<" | "==" | "!=";
  value?: number;
  unit?: string;
  parts?: string[];
  lookup?: { keyBy: string; returns: string; rows: LookupRow[] };
}

export interface VerityRule {
  ruleId: string;
  displayId: string;
  standardId: string;
  sectionId: string;
  docId: string;
  docIndex: number;
  sectionNumber: string;
  sectionTitle: string;
  category: string;

  ruleType: RuleType;
  checkStrategy: CheckStrategy;
  subject: string;
  requirement: string;
  predicate: RulePredicate;

  applicability: { own: string[]; inherited: string[] };
  conditions: string[];
  exceptions: string[];
  externalRefs: string[];
  relations: RuleRelation[];

  // Traceability — never editable, and the reason any of this is worth anything.
  sourceQuote: string;
  charStart: number;
  charEnd: number;
  pdfPage: number;
  printedPage: number | null;

  extractorVersion: number;
  confidence: number;
  /** The machine's verdict. */
  validation: MachineValidation;
  attention: AttentionReason[];
  /** The person's, if one was needed. */
  decision?: HumanDecision;
  derivation?: RuleDerivation;
  derivedFrom?: string[];
  supersededBy?: string[];
  /** Verity's proposed correction, when the validator flagged this rule. A proposal, never applied. */
  repair?: RuleRepair;
  reviewedBy?: string;
  reviewedAt?: string;
}

/**
 * How far a section has got.
 *
 * `no-requirements` is what makes a miss visible: a section nobody opened and a section someone read
 * and found nothing in both hold zero rules, and only one of them is a problem.
 */
export type SectionReviewState =
  | "not-processed" | "candidates" | "in-review" | "reviewed" | "no-requirements"
  /** No rules and no obligation language — cleared without a person. */
  | "machine-cleared"
  /** Obligation language but nothing extracted. A possible miss — a question for a person. */
  | "extraction-gap"
  /**
   * The section could not be read at all — the call failed. Never a person's problem: the answer is
   * to run it again. Kept apart from a gap because putting a machine failure into a human queue is
   * how 146 rate-limited sections of Collingwood came to look like 146 sections needing judgement.
   */
  | "extraction-failed"
  /**
   * DERIVED, never stored. A zero-rule verdict with no proof the model ever answered.
   *
   * Every "possible gap" and "no requirements" written before extraction evidence existed is one of
   * these, because the run that wrote them recorded 146 rate-limited sections as containing nothing.
   * We do not know what is in these sections, so the screen says that instead of inventing a finding.
   */
  | "needs-reprocessing";

export interface SectionReview {
  sectionId: string;
  sectionNumber: string;
  state: SectionReviewState;
  obligationSignals?: number;
  ruleCount?: number;
  /**
   * Proof the model was asked and answered. ABSENT on records written before it existed, and that
   * absence is the point: a gap warning with nothing behind it is not a finding about the document.
   */
  extraction?: {
    completed: boolean;
    attempts: number;
    rejectedByQuoteGate: number;
    extractorVersion: number;
    at: string;
    failure?: string;
  };
  /**
   * The second pass over a zero-rule section: every triggering sentence, judged individually, with a
   * proposed rule where one is warranted. This is what turns a gap from a research task into a
   * decision.
   */
  recovery?: SectionRecovery;
  reviewedBy?: string;
  reviewedAt?: string;
  note?: string;
}

export type RecoveryVerdict =
  | "likely-requirement"
  | "likely-preamble"
  | "likely-definition"
  | "likely-cross-reference"
  | "ambiguous";

export interface RecoveryCandidate {
  id: string;
  quote: string;
  charStart: number;
  charEnd: number;
  pdfPage: number;
  printedPage: number | null;
  verdict: RecoveryVerdict;
  confidence: number;
  assessment: string;
  proposed?: {
    subject: string;
    requirement: string;
    ruleType: RuleType;
    checkStrategy: CheckStrategy;
    predicate: RulePredicate;
    applicability: { own: string[]; inherited: string[] };
    conditions: string[];
    exceptions: string[];
    externalRefs: string[];
  };
  resolution?: {
    action: "accepted" | "rejected";
    by: string;
    at: string;
    ruleId?: string;
    /** Settled without a person. Stated on the card rather than hidden. */
    automatic?: boolean;
  };
}

export interface SectionRecovery {
  version: number;
  at: string;
  candidates: RecoveryCandidate[];
  failure?: string;
}

/** Verity's proposed correction for a flagged requirement. Always a proposal, never applied. */
export interface RuleRepair {
  version: number;
  at: string;
  diagnosis: string;
  rationale: string;
  confidence: number;
  suggestion?: {
    subject?: string;
    requirement?: string;
    ruleType?: RuleType;
    predicate?: RulePredicate;
    externalRefs?: string[];
    exclude?: boolean;
  };
  failure?: string;
}

/** What a published set actually covers — recorded, never inferred. */
export interface RuleSetCoverage {
  chapters: string[];
  sectionIds: string[];
  reviewedSections: number;
  totalSections: number;
  complete: boolean;
  // Kept apart on purpose: a machine check and a human reading are different claims.
  sourceVerified: number;
  humanReviewed: number;
  excluded: number;
  unresolved: number;
}

export interface PublishedRuleSet {
  id: string;
  version: number;
  publishedBy: string;
  publishedAt: string;
  coverage: RuleSetCoverage;
  ruleCount: number;
  hash: string;
}

export interface ProcessingProgress {
  state: "processing" | "complete" | "failed" | "stopped";
  /** A person asked the run to stop; the loop finishes its current section and halts. */
  stopRequested?: boolean;
  sectionsDone?: number;
  sectionsTotal?: number;
  currentSection?: string;
  /** Sections being read right now — what makes a chapter active rather than queued. */
  activeSections?: string[];
  timing?: {
    wallMs: number;
    modelMs: number;
    storageMs: number;
    concurrency: number;
    sectionsTimed: number;
    cacheHits: number;
    /** Passes over the work, counting automatic retries of rate-limited sections. */
    sweeps?: number;
    /** Model calls beyond the first, summed — mostly rate-limit backoff. */
    retries?: number;
    retriedSections?: number;
    meanMs: number;
    medianMs: number;
    slowest: { section: string; ms: number; rules: number }[];
  };
  /** Sections the run could not read. A completed run that dropped work must say so on its own record. */
  failedSections?: string[];
  error?: string;
  updatedAt: string;
}

/** Counts and per-section rollups, without the rule bodies. A few KB instead of 1.5 MB. */
export interface ProgressSnapshot {
  processing: ProcessingProgress | null;
  sectionReviews: SectionReview[];
  counts: {
    rules: number;
    verified: number;
    reviewRecommended: number;
    actionRequired: number;
    humanReviewed: number;
  };
  bySection: Record<string, { total: number; actionRequired: number; reviewRecommended: number }>;
}

export interface ReviewState {
  rules: VerityRule[];
  sectionReviews: SectionReview[];
  ruleSets: PublishedRuleSet[];
  /** Null when the standard has never been read. */
  processing: ProcessingProgress | null;
}

export interface AuditEntry {
  id: string;
  sectionId?: string;
  ruleId?: string;
  action: string;
  summary: string;
  by: string;
  at: string;
}

/** The editable half of a rule. Source fields are deliberately absent. */
export interface RuleEdit {
  subject?: string;
  requirement?: string;
  ruleType?: RuleType;
  checkStrategy?: CheckStrategy;
  predicate?: RulePredicate;
  conditions?: string[];
  exceptions?: string[];
  externalRefs?: string[];
}

/** One of the rules a split will produce, or the single rule a merge collapses to. */
export interface RulePart {
  subject: string;
  requirement: string;
  ruleType?: RuleType;
  checkStrategy?: CheckStrategy;
  predicate?: RulePredicate;
  conditions?: string[];
  exceptions?: string[];
  externalRefs?: string[];
}
